import React, { FC, useContext, useMemo, useState } from 'react';

import { CircleXmark } from '@gravity-ui/icons';
import { Xmark } from '@gravity-ui/icons';
import { Button, Card, Checkbox, Icon, Modal, RadioGroup, useToaster } from '@gravity-ui/uikit';
import i18n from '@yandex-int/i18n';
import clx from 'classnames';
import { ErrorBoundary } from 'client/blocks/ErrorBoundary/ErrorBoundary';
import { Context as AppsMetaInfoContext } from 'client/context/appsMetaInfoContext';
import { Context as SnapshotContext } from 'client/context/snapshotContext';
import { wrapInText } from 'client/lib/i18n-utils';
import { routes } from 'client/lib/routes';
import { Channel } from 'client/model/skill';
import { isInstallableAppChannel } from 'client/utils';
import * as aliceSkillFormKeyset from 'i18n/AliceSkillForm';
import * as keyset from 'i18n/AppsIndex';

import { Link } from '../Link/Link';

import styles from './AddNewAppCard.module.scss';
import { AutoAppIcon, PlusIcon, SmartScreenAppIcon, TvAppIcon } from './AddNewAppIcons';
import { SkillIcon } from './AddNewSkillIcon';

interface AddNewAppCardProps {
    channel: Channel;
    aliceAppsCount: number;
}

interface ChannelContent {
    createLabel: string;
    icon: React.JSX.Element | null;
    shortDescription: string;
}

const PorgStatus = {
    Private: 'private',
    Porg: 'porg',
} as const;
type TPorgStatus = (typeof PorgStatus)[keyof typeof PorgStatus];

const t = i18n(keyset);
const tAliceSkillForm = i18n(aliceSkillFormKeyset);

const mapCreateAppError = (error: unknown) => {
    if (
        !!error &&
        typeof error === 'object' &&
        'message' in error &&
        typeof error.message === 'string' &&
        error.message?.includes('Max app per user violation')
    )
        return t('max-apps-error');
    return tAliceSkillForm('error-occurred');
};

const getMapChannelToContent = (): Record<Channel, ChannelContent> => ({
    [Channel.AutoApp]: {
        createLabel: t('create-auto-app'),
        icon: <AutoAppIcon />,
        shortDescription: 'Auto',
    },
    [Channel.TvApp]: {
        createLabel: t('create-tv-app'),
        icon: <TvAppIcon />,
        shortDescription: 'YaOs',
    },
    [Channel.SmartScreenApp]: {
        createLabel: t('create-smart-screen-app'),
        icon: <SmartScreenAppIcon />,
        shortDescription: 'Duo',
    },
    [Channel.AliceSkill]: {
        createLabel: t('create-alice-skill'),
        icon: <SkillIcon channel={Channel.AliceSkill} />,
        shortDescription: 'AliceSkill',
    },
    [Channel.SmartHome]: {
        createLabel: t('create-smart-home'),
        icon: <SkillIcon channel={Channel.SmartHome} />,
        shortDescription: 'SmartHome',
    },
});

const Warning: FC<{ porgStatus: TPorgStatus }> = ({ porgStatus }) => {
    const title = porgStatus === PorgStatus.Private ? t('you-are-private') : t('you-are-porg');
    const linkLabel = porgStatus === PorgStatus.Private ? t('how-to-login-as-porg') : t('how-to-login-as-private');

    return (
        <Card className={styles.createApp__card} theme="danger">
            <Icon className={styles.createApp__redAccent} data={CircleXmark} />
            <div>
                <p className={styles.createApp__redAccent}>{title}</p>
                <div className={styles.createApp__link}>
                    {wrapInText((s) => (
                        <Link external target="_blank" theme="info" to={routes.documentation.aliceApps.createApp}>
                            {s}
                        </Link>
                    ))(linkLabel)}
                </div>
            </div>
        </Card>
    );
};

export const AddNewAppCard = ({ channel, aliceAppsCount }: AddNewAppCardProps) => {
    const [isOpen, setIsOpen] = useState(false);
    const [termsOfUseAreAccepted, setTermsOfUseAreAccepted] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const { user } = __appConfig;
    const currentPorgStatus = user.porgName ? PorgStatus.Porg : PorgStatus.Private;
    const [selectedPorgStatus, setSelectedPorgStatus] = useState<TPorgStatus>(currentPorgStatus);

    const { appsMetaInfo, handleAppCreation } = useContext(AppsMetaInfoContext);
    const { snapshot, createSkill } = useContext(SnapshotContext);
    const { add: toasterAdd } = useToaster();

    const mapChannelToContent = useMemo(() => getMapChannelToContent(), []);

    const createAliceApp = isInstallableAppChannel(channel) ? handleAppCreation : createSkill;

    const disabled = isInstallableAppChannel(channel)
        ? aliceAppsCount >= appsMetaInfo?.maxAppsPerUser
        : snapshot?.skills?.length >= snapshot?.maxSkillsPerUser;

    const content = mapChannelToContent?.[channel];

    const handleClick = () => {
        setIsOpen(true);
    };

    const handleCreateAliceApp = async () => {
        if (isLoading) return;

        setIsLoading(true);
        try {
            await createAliceApp(channel);
        } catch (error) {
            setIsOpen(false);
            toasterAdd({ title: mapCreateAppError(error), theme: 'danger', name: 'CREATE_APP' });
        }
        setIsLoading(false);
    };

    if (!content) return null;

    if (channel === Channel.AliceSkill || channel === Channel.SmartHome) {
        return (
            <ErrorBoundary>
                <div
                    className={clx(
                        styles.addNewAppCard__borderWrapper,
                        (disabled || isLoading) && styles.addNewAppCard_disabled,
                    )}
                >
                    <div
                        className={clx(
                            styles.addNewAppCard__container,
                            disabled && styles.addNewAppCard__container_disabled,
                            isLoading && styles.addNewAppCard__container_loading,
                            channel === Channel.AliceSkill && styles.addNewAppCard__container_aliceSkill,
                            channel === Channel.SmartHome && styles.addNewAppCard__container_smartHome,
                        )}
                        onClick={handleCreateAliceApp}
                    >
                        <div
                            className={clx(
                                styles.addNewAppCard__contentContainer,
                                styles.addNewAppCard__contentContainer_aliceSkill,
                            )}
                        >
                            <PlusIcon />
                            <h4
                                className={clx(
                                    styles.gradientText,
                                    styles.addNewAppCard__title,
                                    styles.addNewAppCard__title_aliceSkill,
                                )}
                            >
                                {content.createLabel}
                            </h4>
                        </div>
                        <div
                            className={clx(
                                styles.addNewAppCard__iconContainer,
                                channel === Channel.AliceSkill && styles.addNewAppCard__iconContainer_aliceSkill,
                                channel === Channel.SmartHome && styles.addNewAppCard__iconContainer_smartHome,
                            )}
                        >
                            <div className={styles.gradientIcon__container}>{content.icon}</div>
                        </div>
                    </div>
                </div>
            </ErrorBoundary>
        );
    }

    const createButtonDisabled = selectedPorgStatus !== currentPorgStatus || !termsOfUseAreAccepted;

    return (
        <ErrorBoundary>
            <div className={clx(styles.addNewAppCard__borderWrapper, disabled && styles.addNewAppCard_disabled)}>
                <div className={styles.addNewAppCard__container} onClick={handleClick}>
                    <div className={styles.addNewAppCard__contentContainer}>
                        <PlusIcon />
                        <h4 className={clx(styles.gradientText, styles.addNewAppCard__title)}>{content.createLabel}</h4>
                    </div>
                    <div className={styles.addNewAppCard__iconContainer}>
                        <div className={styles.gradientIcon__container}>{content.icon}</div>
                    </div>
                </div>
            </div>
            <Modal open={isOpen} onOutsideClick={() => setIsOpen(false)}>
                <div className={styles.createApp__container}>
                    <header className={styles.createApp__header}>
                        <h4>{t('from-whom-you-want-to-create')}</h4>
                        <Button className={styles.closeButton} onClick={() => setIsOpen(false)} size="l" view="flat">
                            <Icon data={Xmark} size={16} />
                        </Button>
                    </header>
                    <RadioGroup
                        className={styles.createApp__radioGroup}
                        value={selectedPorgStatus}
                        onUpdate={() =>
                            setSelectedPorgStatus((state) =>
                                state === PorgStatus.Porg ? PorgStatus.Private : PorgStatus.Porg,
                            )
                        }
                        direction="vertical"
                        size="l"
                    >
                        <RadioGroup.Option value={PorgStatus.Private}>{t('private-person')}</RadioGroup.Option>
                        <RadioGroup.Option value={PorgStatus.Porg}>{t('porg-representative')}</RadioGroup.Option>
                    </RadioGroup>
                    {selectedPorgStatus !== currentPorgStatus ? <Warning porgStatus={currentPorgStatus} /> : null}
                    <Checkbox
                        checked={termsOfUseAreAccepted}
                        onChange={() => setTermsOfUseAreAccepted((state) => !state)}
                        className={styles.createApp__termsOfUse}
                        size="l"
                    >
                        {wrapInText((s) => (
                            <Link external target="_blank" theme="info" to={routes.documentation.termsOfUse}>
                                {s}
                            </Link>
                        ))(t('terms-of-use-checkbox-label'))}
                    </Checkbox>
                    <footer className={styles.createApp__footer}>
                        <Button onClick={() => setIsOpen(false)} size="l" disabled={isLoading}>
                            {t('cancel')}
                        </Button>
                        <Button
                            onClick={handleCreateAliceApp}
                            disabled={createButtonDisabled}
                            view="action"
                            size="l"
                            loading={isLoading}
                        >
                            {t('create')}
                        </Button>
                    </footer>
                </div>
            </Modal>
        </ErrorBoundary>
    );
};
